import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSharedState } from 'sharedStateProvider';
import { UserType, UsersResponseType, emptyUser } from 'types/user-types';
import { emptyPagination } from 'types/pagination-types';
import RemoveNonNumericCharacters from 'components/helpers/remove-non-numeric';
import PageLayout from 'components/page-layout';
import Table, { tableHeader } from 'components/tables/table';
import {
  avatarWithTwoLines,
  oneLine,
  twoLines,
  mutedLine,
} from 'components/tables/table-parts';
import NewUser from 'components/modals/new-user';
import DangerModal from 'components/modals/danger';
import NewAdmin from 'components/modals/new-admin';
import { getAdmins } from 'api/users';
import ActionButton from 'components/tables/action-btn';
import {
  IconLock,
  IconLockOpen,
  IconUser,
  IconUserEdit,
  IconUserX,
} from '@tabler/icons-react';
import { useUserEdit } from './use-user-edit';
import { getApiErrorMessage } from 'api/error';

export default function AdminsList() {
  const navigate = useNavigate();
  const { isSuperAdmin, notify } = useSharedState();
  const [users, setUsers] = useState<UsersResponseType>(emptyPagination);
  const [firstEntry, setFirstEntry] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [searchParams] = useState<URLSearchParams>(new URLSearchParams());
  const updateFirstEntry = (value: number) => {
    setFirstEntry(value);
  };

  const [editingUser, setEditingUser] = useState<UserType>(emptyUser);

  const getAndSetUsers = useCallback(async () => {
    setIsLoading(true);
    setUsers(emptyPagination);

    try {
      const res: UserType[] = await getAdmins(searchParams);
      setUsers({
        data: res,
        page: 1,
        size: 25,
        sort: '',
        total: res.length,
      });
      window.scrollTo(0, 0);
    } catch (error: unknown) {
      notify(getApiErrorMessage(error, 'Ошибка при загрузке администраторов'), 'danger', 'Ошибка при загрузке администраторов');
    } finally {
      setIsLoading(false);
    }
  }, [notify, searchParams]);

  const { dangerDescription, confirmDanger, onDangerModal } = useUserEdit({
    onChange: getAndSetUsers,
    isAdmin: true,
  });

  const editUserData = (user: UserType) => {
    setEditingUser(user);
  };

  const appendParam = (key: string, value: string) => {
    if (searchParams.has(key)) {
      searchParams.set(key, value);
    } else {
      searchParams.append(key, value);
    }
  };

  const clearFilters = useCallback(async (param?: string) => {
    if (param) {
      param && searchParams.delete(param);
    } else {
      searchParams.delete('type');
      searchParams.delete('status');
      searchParams.delete('search');
    }
  }, [searchParams]);

  useEffect(() => {
    clearFilters();
    getAndSetUsers();
  }, [clearFilters, getAndSetUsers]);

  const usersTableHeaders: tableHeader[] = [
    { header_text: '№', narrow: true, wrap: false },
    { header_text: 'Пользователь', narrow: false, wrap: false },
    { header_text: 'Предприятие, подразделение', narrow: false, wrap: true },
    { header_text: 'Должность', narrow: false, wrap: false },
    { header_text: 'Тип', narrow: false, wrap: false },
    { header_text: 'Статус', narrow: false, wrap: false },
    { header_text: 'Создан', narrow: false, wrap: false },
    { header_text: 'Изменён', narrow: false, wrap: false },
    { header_text: '', narrow: true, wrap: false },
  ];

  const usersList = (
    <>
      {users.data.map((user, index) => (
        <tr key={index}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>
            {avatarWithTwoLines(
              user.avatar,
              'user',
              `${user.first_name} ${user.last_name}`,
              user.phone
                ? `+${RemoveNonNumericCharacters(user.phone)}`
                : 'Нет номера'
            )}
          </td>
          <td>
            {twoLines(`${user.enterprise || '-'}`, `${user.org_unit || '-'}`)}
          </td>
          <td>{oneLine(`${user.position || '-'}`)}</td>
          <td>{mutedLine(`${user.user_type || '-'}`)}</td>
          <td>
            {oneLine(
              `${user.status || '-'}`,
              user.status === 'active'
                ? 'success'
                : user.status === 'inactive'
                ? 'danger'
                : 'info'
            )}
          </td>
          <td>{oneLine(`${new Date(user.created_at).toLocaleString()}`)}</td>
          <td>{oneLine(`${new Date(user.updated_at).toLocaleString()}`)}</td>
          <td>
            <div className="btn-list flex-nowrap">
              <ActionButton
                icon={<IconUser className="icon" />}
                tooltip="Профиль"
                action={() => navigate(`/user/${user.id}`)}
                color={'info'}
                modal={false}
              />
              {isSuperAdmin && (
                <ActionButton
                  icon={<IconUserEdit className="icon" />}
                  tooltip="Изменить"
                  action={() => editUserData(user)}
                  color={'info'}
                  modal={true}
                  modal_id="#modal-new-user"
                />
              )}
              {isSuperAdmin && (
                <ActionButton
                  icon={
                    user.status === 'inactive' ? (
                      <IconLockOpen className="icon" />
                    ) : (
                      <IconLock className="icon" />
                    )
                  }
                  tooltip={`${
                    user.status === 'inactive'
                      ? 'Разблокировать'
                      : 'Заблокировать'
                  }`}
                  action={() =>
                    onDangerModal(
                      user,
                      user.status === 'inactive' ? 'unblock' : 'block'
                    )
                  }
                  color={`${user.status === 'inactive' ? 'success' : 'danger'}`}
                  modal={true}
                  modal_id="#modal-danger"
                />
              )}
              {isSuperAdmin && (
                <ActionButton
                  icon={<IconUserX className="icon" />}
                  tooltip="Удалить"
                  action={() => onDangerModal(user, 'delete')}
                  color={'danger'}
                  modal={true}
                  modal_id="#modal-danger"
                />
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const usersTable = Table(
    usersTableHeaders,
    usersList,
    true,
    users,
    updateFirstEntry,
    getAndSetUsers
  );

  const usersHeader = 'Администраторы';
  const usersSubheaderText = `Количество администраторов`;
  const usersSubheaderCount = users.total;
  const headerButtonText = isSuperAdmin ? 'Добавить администратора' : undefined;
  const headerButtonModal = `#modal-new-${isSuperAdmin ? 'admin' : 'user'}`;

  return (
    <>
      <PageLayout
        children={usersTable}
        isLoading={isLoading}
        filters={false}
        appendFilter={appendParam}
        clearFilters={clearFilters}
        search={true}
        searchAPI={getAndSetUsers}
        header={usersHeader}
        subheaderText={usersSubheaderText}
        subheaderCount={usersSubheaderCount}
        dataLength={users.data.length}
        buttonText={headerButtonText}
        buttonModal={headerButtonModal}
        dateRange={false}
        getExcel={false}
        excelParams={searchParams}
        excelCount={users.total}
      />
      <NewUser
        userData={editingUser}
        updateUsers={getAndSetUsers}
        cancelEdit={editUserData}
      />
      <NewAdmin updateUsers={getAndSetUsers} />
      <DangerModal
        dangerDescription={dangerDescription}
        confirm={confirmDanger}
      />
    </>
  );
}
